import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Row, Col, Button, Alert, Spinner, Badge, ProgressBar, Modal, Form } from 'react-bootstrap';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import AppScriptAPI from '../services/appscriptAPI';
import AppToast from '../components/AppToast';
import { useUserPermissions } from '../contexts/UserPermissionsContext';

// ─── Section definitions ─────────────────────────────────────────────────────
const SECTIONS = [
  { key: 'basic',          label: 'Basic Information',      api: (id, sh) => AppScriptAPI.getBasicInfo(id, sh) },
  { key: 'contact',        label: 'Contact Details',        api: (id, sh) => AppScriptAPI.getContactDetails(id, sh) },
  { key: 'personal',       label: 'Personal Details',       api: (id, sh) => AppScriptAPI.getPersonalDetails(id, sh) },
  { key: 'housing',        label: 'Housing & Ownership',    api: (id, sh) => AppScriptAPI.getHousingDetails(id, sh) },
  { key: 'disability',     label: 'Disability Information', api: (id, sh) => AppScriptAPI.getDisabilityDetails(id, sh) },
  { key: 'education',      label: 'Education Details',      api: (id, sh) => AppScriptAPI.getEducationDetails(id, sh) },
  { key: 'vocational',     label: 'Vocational Training',    api: (id, sh) => AppScriptAPI.getVocationalTrainingDetails(id, sh) },
  { key: 'economic',       label: 'Economic Activity',      api: (id, sh) => AppScriptAPI.getEconomicActivityDetails(id, sh) },
  { key: 'assistance',     label: 'Assistance from Others', api: (id, sh) => AppScriptAPI.getAssistanceDetails(id, sh) },
  { key: 'home',           label: 'Contribution at Home',   api: (id, sh) => AppScriptAPI.getContributionAtHomeDetails(id, sh) },
  { key: 'welfare',        label: 'Welfare Assistance',     api: (id, sh) => AppScriptAPI.getWelfareAssistanceDetails(id, sh) },
  { key: 'rehabilitation', label: 'Rehabilitation Plan',    api: (id, sh) => AppScriptAPI.getRehabilitationPlanDetails(id, sh) },
];

// A4 at 96 dpi ≈ 794px wide. We render at scale=2 so effective width = 1587px.
// jsPDF A4 in mm: 210 × 297. We use 10 mm margins top/bottom.
const PDF_PAGE_W_MM  = 210;
const PDF_PAGE_H_MM  = 297;
const PDF_MARGIN_MM  = 10;
const PDF_USABLE_H   = PDF_PAGE_H_MM - PDF_MARGIN_MM * 2; // 277 mm

// ─── Main Component ──────────────────────────────────────────────────────────
function PersonalSummary() {
  const { recordId } = useParams();
  const [searchParams] = useSearchParams();
  const sheetId = searchParams.get('sheetId');
  const navigate = useNavigate();

  // One ref per section so we can capture them individually for PDF
  const headerRef = useRef();
  const footerRef = useRef();
  const sectionRefs = useRef({});

  const { userPermissions } = useUserPermissions();

  const [data, setData] = useState({});
  const [sectionStatus, setSectionStatus] = useState({});
  const [loadedCount, setLoadedCount] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [fatalError, setFatalError] = useState('');

  const [showDeceasedModal, setShowDeceasedModal] = useState(false);
  const [confirmInput, setConfirmInput]           = useState('');
  const [deathDate, setDeathDate]                 = useState('');
  const [markingDeceased, setMarkingDeceased]     = useState(false);
  const [toast, setToast]                         = useState(null);

  useEffect(() => {
    if (!recordId || !sheetId) {
      setFatalError('Missing required parameters. Please search for the record again.');
      return;
    }
    fetchAllSections();
  }, [recordId, sheetId]);

  const fetchAllSections = async () => {
    const init = {};
    SECTIONS.forEach((s) => { init[s.key] = 'pending'; });
    setSectionStatus(init);
    let count = 0;
    for (const section of SECTIONS) {
      setSectionStatus((prev) => ({ ...prev, [section.key]: 'loading' }));
      try {
        const res = await section.api(recordId, sheetId);
        if (res.success) {
          setData((prev) => ({ ...prev, [section.key]: res }));
          setSectionStatus((prev) => ({ ...prev, [section.key]: 'done' }));
        } else {
          setSectionStatus((prev) => ({ ...prev, [section.key]: 'error' }));
        }
      } catch {
        setSectionStatus((prev) => ({ ...prev, [section.key]: 'error' }));
      }
      count++;
      setLoadedCount(count);
    }
  };

  // ── PDF: capture each block separately, place section-aware onto pages ──
  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const pdf        = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      // scale 1.5 gives good clarity at A4 without the 4× pixel cost of scale 2.
      // JPEG at 0.82 quality cuts file size by ~70% vs PNG with no visible difference for text.
      const scale       = 1.5;
      const captureOpts = { scale, useCORS: true, backgroundColor: '#ffffff', logging: false };

      // Helper: capture a DOM element → returns { imgData, wMM, hMM }
      const capture = async (el) => {
        const canvas  = await html2canvas(el, captureOpts);
        const imgData = canvas.toDataURL('image/jpeg', 0.82); // JPEG, not PNG
        const wMM     = PDF_PAGE_W_MM - PDF_MARGIN_MM * 2;
        const hMM     = (canvas.height / canvas.width) * wMM;
        return { imgData, wMM, hMM };
      };

      let cursorY = PDF_MARGIN_MM; // current Y position on the page (mm)

      const placeBlock = async (el) => {
        if (!el) return;
        const { imgData, wMM, hMM } = await capture(el);

        // If this block alone is taller than a full page, just place it and let
        // it overflow — better than an infinite loop. Otherwise start a new page
        // if it won't fit in remaining space.
        if (hMM > PDF_USABLE_H) {
          // Place at current cursor, it will overflow the page — acceptable edge case
          pdf.addImage(imgData, 'JPEG', PDF_MARGIN_MM, cursorY, wMM, hMM);
          pdf.addPage();
          cursorY = PDF_MARGIN_MM;
        } else if (cursorY + hMM > PDF_PAGE_H_MM - PDF_MARGIN_MM) {
          // Doesn't fit on current page → new page
          pdf.addPage();
          cursorY = PDF_MARGIN_MM;
          pdf.addImage(imgData, 'JPEG', PDF_MARGIN_MM, cursorY, wMM, hMM);
          cursorY += hMM + 4; // 4 mm gap between blocks
        } else {
          pdf.addImage(imgData, 'JPEG', PDF_MARGIN_MM, cursorY, wMM, hMM);
          cursorY += hMM + 4;
        }
      };

      // 1. Document header
      await placeBlock(headerRef.current);

      // 2. Each section (only the ones that loaded successfully)
      for (const section of SECTIONS) {
        const el = sectionRefs.current[section.key];
        if (el && sectionStatus[section.key] === 'done') {
          await placeBlock(el);
        }
      }

      // 3. Footer
      await placeBlock(footerRef.current);

      pdf.save(`DoSS_Summary_${data.basic?.record?.uniqueId || recordId}.pdf`);
    } catch (err) {
      alert('Failed to generate PDF: ' + err.message);
    } finally {
      setDownloading(false);
    }
  };

  const progress          = Math.round((loadedCount / SECTIONS.length) * 100);
  const allDone           = loadedCount === SECTIONS.length;
  const r                 = (key) => data[key]?.record || {};
  const isDeceased        = !!data.basic?.record?.deceased;
  const canMarkDeceased   = userPermissions?.access_type === 'editor' || userPermissions?.access_type === 'admin';

  const handleMarkDeceased = async () => {
    setMarkingDeceased(true);
    try {
      const res = await AppScriptAPI.markAsDeceased(recordId, sheetId, deathDate);
      if (!res.success) throw new Error(res.message || 'Failed to mark as deceased');
      setShowDeceasedModal(false);
      setConfirmInput('');
      setDeathDate('');
      setToast({ type: 'success', msg: 'Record marked as deceased.' });
      fetchAllSections();
    } catch (e) {
      setToast({ type: 'danger', msg: 'Error: ' + e.message });
    } finally {
      setMarkingDeceased(false);
    }
  };

  if (fatalError) {
    return (
      <div className="container mt-5">
        <Alert variant="danger">{fatalError}</Alert>
        <Button variant="secondary" onClick={() => navigate('/search')}><><i className="bi bi-arrow-left me-1"></i>Back to Search</></Button>
      </div>
    );
  }

  return (
    <div className="container mt-5 mb-5" style={{ minHeight: '100vh' }}>
      <div className="row justify-content-center">
        <div className="col-lg-9 col-md-11">

          {/* Action bar — not part of PDF */}
          <AppToast show={!!toast} type={toast?.type} message={toast?.msg} onClose={() => setToast(null)} />

          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <Button variant="outline-secondary" size="sm" onClick={() => navigate('/search')}>
              <><i className="bi bi-arrow-left me-1"></i>Back to Search</>
            </Button>
            <div className="d-flex gap-2 flex-wrap">
              {data.basic?.record && !isDeceased && (
                <Button variant="outline-success" size="sm"
                  onClick={() => navigate(`/update/basic/${recordId}?sheetId=${sheetId}`)}>
                  <><i className="bi bi-pencil me-1"></i>Update Details</>
                </Button>
              )}
              {canMarkDeceased && !isDeceased && data.basic?.record && (
                <Button variant="outline-danger" size="sm" onClick={() => setShowDeceasedModal(true)}>
                  <><i className="bi bi-x-circle me-1"></i>Mark as Deceased</>
                </Button>
              )}
              <Button variant="outline-primary" size="sm"
                onClick={handleDownloadPDF}
                disabled={downloading || !allDone}
                title={!allDone ? 'Wait for all sections to load' : ''}>
                {downloading
                  ? <><Spinner animation="border" size="sm" className="me-1" />Generating...</>
                  : <><i className="bi bi-download me-1"></i>Download PDF</>}
              </Button>
            </div>
          </div>

          {/* Loading progress — not part of PDF */}
          {!allDone && (
            <Card className="mb-3 border-0 shadow-sm">
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between mb-2">
                  <span className="fw-semibold" style={{ fontSize: '0.9rem' }}>Loading sections...</span>
                  <span className="text-muted" style={{ fontSize: '0.85rem' }}>{loadedCount} / {SECTIONS.length}</span>
                </div>
                <ProgressBar now={progress} animated striped variant="primary" style={{ height: '8px' }} />
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {SECTIONS.map((s) => <SectionPill key={s.key} label={s.label} status={sectionStatus[s.key]} />)}
                </div>
              </Card.Body>
            </Card>
          )}

          {/* ── Document header (captured separately for PDF) ── */}
          <div ref={headerRef}
            style={{ background: '#fff', padding: '16px 24px 8px', borderBottom: '1px solid #dee2e6', marginBottom: '4px' }}>
            {isDeceased && (
              <div style={{
                background: '#dc3545', color: '#fff', fontWeight: 700,
                textAlign: 'center', padding: '6px 12px', borderRadius: '4px',
                marginBottom: '10px', letterSpacing: '0.06em', fontSize: '0.85rem',
              }}>
                <i className="bi bi-x-circle-fill me-2"></i>
                DECEASED — {data.basic?.record?.deceasedDate}
              </div>
            )}
            <div className="text-center">
              <h4 className="fw-bold mb-1">Department of Social Services</h4>
              <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                National CBR Program — Database Management System
              </p>
              <h5 className="mt-2 mb-1">Personal Summary Report</h5>
              {data.basic?.record && (
                <div style={{ fontSize: '0.9rem', color: '#555', marginTop: '4px' }}>
                  DoSS ID: <strong>{r('basic').uniqueId}</strong>
                </div>
              )}
            </div>
          </div>

          {/* ── Sections (each captured separately for PDF) ── */}
          <div style={{ background: '#fff' }}>

            {/* 1 — Basic */}
            <SectionBlock sectionKey="basic" label="Basic Information"
              status={sectionStatus.basic}
              sectionRefs={sectionRefs}>
              <Row className="g-2">
                <Field label="Province"    value={r('basic').province}   col={4} />
                <Field label="District"    value={r('basic').district}   col={4} />
                <Field label="DS Division" value={r('basic').dsDivision} col={4} />
                <Field label="GN Division" value={r('basic').gnDivision} col={6} />
                <Field label="Full Name"   value={r('basic').name}       col={6} />
                <Field label="NIC Number"  value={r('basic').nic}        col={6} />
              </Row>
            </SectionBlock>

            {/* 2 — Contact */}
            <SectionBlock sectionKey="contact" label="Contact Details"
              status={sectionStatus.contact} sectionRefs={sectionRefs}>
              <Row className="g-2">
                <Field label="Address"         value={r('contact').address}   col={12} freeText />
                <Field label="Home Phone Number" value={r('contact').phoneHome} col={4} />
                <Field label="Mobile Number"   value={r('contact').mobile}    col={4} />
                <Field label="Email Address"   value={r('contact').email}     col={4} />
              </Row>
            </SectionBlock>

            {/* 3 — Personal */}
            <SectionBlock sectionKey="personal" label="Personal Details"
              status={sectionStatus.personal} sectionRefs={sectionRefs}>
              <Row className="g-2">
                <Field label="Date of Birth"                           value={r('personal').dateOfBirth}           col={4} />
                <Field label="Gender"                                  value={r('personal').gender}                col={4} />
                <Field label="Civil Status"                            value={r('personal').civilStatus}           col={4} />
                <Field label="Religion"                                value={r('personal').religion}              col={4} />
                <Field label="Nationality"                             value={r('personal').race}                  col={4} />
                <Field label="Occupation"                              value={r('personal').occupation}            col={4} />
                <Field label="Education Level"                         value={r('personal').educationLevel}        col={6} />
                <Field label="Relationship with Head of Household"     value={r('personal').relationshipHoH}      col={6} />
                <Field label="Are there any certificates of identity?" value={r('personal').identityCertificates} col={6} />
                <Field label="Document or ID Number"                   value={r('personal').documentNumber}       col={3} />
                <Field label="Is there a pension benefit?"             value={r('personal').pensionBenefit}       col={3} />
              </Row>
            </SectionBlock>

            {/* 4 — Housing */}
            <SectionBlock sectionKey="housing" label="Housing & Ownership"
              status={sectionStatus.housing} sectionRefs={sectionRefs}>
              <Row className="g-2">
                <Field label="Do you have a house?"                value={r('housing').hasHouse}       col={4} />
                <Field label="House Ownership"                     value={r('housing').houseOwnership} col={4} />
                <Field label="Type of the House"                   value={r('housing').houseType}      col={4} />
                <Field label="The Condition of the House"          value={r('housing').houseCondition} col={4} />
                <Field label="Do you have a Land?"                 value={r('housing').hasLand}        col={4} />
                <Field label="Nature of Ownership of the Land"     value={r('housing').landOwnership}  col={4} />
                <Field label="Family Monthly Average Income"       value={r('housing').monthlyIncome}  col={6} />
              </Row>
            </SectionBlock>

            {/* 5 — Disability */}
            <SectionBlock sectionKey="disability" label="Disability Information"
              status={sectionStatus.disability} sectionRefs={sectionRefs}>
              <Row className="g-2">
                <Field label="What kind of disability?"                                  value={r('disability').disabilityType}      col={6} />
                <Field label="Defective Nature"                                          value={r('disability').defectiveNature}     col={6} />
                <Field label="Cause of Disability"                                       value={r('disability').causeOfDisability}   col={6} />
                <Field label="Period of Disability (Years)"                              value={r('disability').periodOfDisability}  col={3} />
                <Field label="Does the person use sign language?"                        value={r('disability').usesSignLanguage}    col={3} />
                <Field label="Do you use Braille?"                                       value={r('disability').usesBraille}         col={3} />
                <Field label="Medical Treatment for Disability"                          value={r('disability').medicalTreatment}    col={6} />
                <Field label="Monthly Cost of Medical Follow Up"                         value={r('disability').monthlyCost}         col={3} />
                <Field label="From which kind of hospital do you get medical treatment?" value={r('disability').hospitalType}        col={6} />
                <Field label="Are you using any assistive devices?"                      value={r('disability').usesAssistiveDevice} col={6} />
                <Field label="Type of Equipment Used / Equipment Wanted to Use"          value={r('disability').assistiveDeviceType} col={6} />
                <Field label="Equipment Issued By"                                       value={r('disability').equipmentIssuedBy}   col={6} />
                <Field label="Condition of Equipment"                                    value={r('disability').equipmentCondition}  col={6} />
              </Row>
            </SectionBlock>

            {/* 6 — Education */}
            <SectionBlock sectionKey="education" label="Education Details"
              status={sectionStatus.education} sectionRefs={sectionRefs}>
              <Row className="g-2">
                <Field label="Education Status"                                      value={r('education').educationStatus}     col={6} />
                <Field label="Education Level"                                       value={r('education').educationLevel}      col={6} />
                <Field label="Where are you studying?"                               value={r('education').studyingLocation}    col={4} />
                <Field label="What kind of school are you attending / attended?"     value={r('education').schoolType}          col={4} />
                <Field label="Reason for school dropout?"                            value={r('education').dropoutReason}       col={4} />
                <Field label="Literacy Level"                                        value={r('education').literacyLevel}       col={6} />
                <Field label="Are you using any assistive devices for education?"    value={r('education').usesEducationDevice} col={6} />
              </Row>
            </SectionBlock>

            {/* 7 — Vocational */}
            <SectionBlock sectionKey="vocational" label="Vocational Training"
              status={sectionStatus.vocational} sectionRefs={sectionRefs}>
              <Row className="g-2">
                <Field label="Status of Vocational Training"                                                      value={r('vocational').vocationalStatus}         col={6} />
                <Field label="What kind of vocational training is doing / was obtained?"                          value={r('vocational').trainingKind}             col={6} />
                <Field label="Organisation Name"                                                                  value={r('vocational').organizationName}         col={6} />
                <Field label="Training received from which government organisation?"                              value={r('vocational').govOrganizations}         col={6} />
                <Field label="Vocational Training Institutions of the Department of Social Services"              value={r('vocational').dossInstitutions}         col={6} />
                <Field label="Completed Vocational Training Course from DoSS"                                     value={r('vocational').completedDossCourses}     col={6} />
                <Field label="Vocational Training Institutions of the Ministry of Skill Development"              value={r('vocational').ministryInstitutions}     col={6} />
                <Field label="Completed Vocational Training from Ministry of Skill Development"                   value={r('vocational').completedMinistryCourses} col={6} />
                <Field label="Are you also involved in the occupation related to the vocational training obtained?" value={r('vocational').relatedOccupation}       col={6} />
                <Field label="Nature of Profession"                                                               value={r('vocational').natureOfProfession}       col={6} />
                <Field label="Reason for engaging in occupation unrelated to the training obtained"               value={r('vocational').unrelatedReason}          col={6} />
                <Field label="Reason for non-completion / giving up the course"                                   value={r('vocational').discontinuedReason}       col={6} />
                <Field label="Reason for not following any vocational training course"                            value={r('vocational').notFollowingReason}       col={6} />
                <Field label="Do you have a difficulty to get any vocational training?"                           value={r('vocational').hasDifficulty}            col={6} />
                <Field label="Reasons for having difficulty to get vocational training"                           value={r('vocational').difficultyReasons}        col={6} />
                <Field label="Do you need any career guidance?"                                                   value={r('vocational').needsCareerGuidance}      col={6} />
              </Row>
            </SectionBlock>

            {/* 8 — Economic */}
            <SectionBlock sectionKey="economic" label="Economic Activity"
              status={sectionStatus.economic} sectionRefs={sectionRefs}>
              <Row className="g-2">
                <Field label="Date of Birth"                                                                       value={r('economic').dateOfBirth}        col={4} />
                <Field label="Do you work or do business?"                                                         value={r('economic').isEmployed}         col={4} />
                <Field label="What kind of occupation?"                                                            value={r('economic').occupation}         col={4} />
                <Field label="What type of Self-employment?"                                                       value={r('economic').selfEmploymentType} col={6} />
                <Field label="What is the main reason for non-availability of job / self-employment opportunity?"  value={r('economic').unemploymentReason} col={6} />
                <Field label="Personal Monthly Income"                                                             value={r('economic').monthlyIncome}      col={6} />
              </Row>
            </SectionBlock>

            {/* 9 — Assistance */}
            <SectionBlock sectionKey="assistance" label="Assistance from Others"
              status={sectionStatus.assistance} sectionRefs={sectionRefs}>
              <Row className="g-2">
                <Field label="Do you need assistance from others?"   value={r('assistance').needsAssistance}    col={6} />
                <Field label="A person's assistance in walking"      value={r('assistance').walkingAssistance}  col={6} />
                <Field label="Full-time caretaker"                   value={r('assistance').fulltimeCaretaker}  col={6} />
                <Field label="Consultation"                          value={r('assistance').consultation}       col={6} />
                <Field label="Guidance for daily activities"         value={r('assistance').dailyGuidance}      col={6} />
                <Field label="Sign Language Interpretations"         value={r('assistance').signLanguageInterp} col={6} />
                <Field label="Part-time caretaker"                   value={r('assistance').parttimeCaretaker}  col={6} />
              </Row>
            </SectionBlock>

            {/* 10 — Home */}
            <SectionBlock sectionKey="home" label="Contribution at Home"
              status={sectionStatus.home} sectionRefs={sectionRefs}>
              <Row className="g-2">
                <Field label="In-house access / access facilities status"    value={r('home').accessFacilitiesStatus} col={6} />
                <Field label="Status / Standard of current access"           value={r('home').currentAccessStandard}  col={6} />
                <Field label="Access / Support Areas needed"                 value={r('home').accessAreasNeeded}      col={6} />
                <Field label="Necessary facilities"                          value={r('home').necessaryFacilities}    col={6} />
                <Field label="Do you have toilet facilities?"                value={r('home').hasToilet}              col={4} />
                <Field label="Type of toilet"                                value={r('home').toiletType}             col={4} />
                <Field label="Is the toilet suitable for the disabled?"      value={r('home').toiletSuitable}         col={4} />
                <Field label="Changes to be made to toilet"                  value={r('home').toiletChangesNeeded}    col={6} />
                <Field label="Reason for not having toilet"                  value={r('home').noToiletReason}         col={6} />
                <Field label="Type of toilet suitable for the disabled"      value={r('home').suitableToiletType}     col={6} />
                <Field label="Necessary changes for toilet setup"            value={r('home').toiletSetupChanges}     col={6} />
              </Row>
            </SectionBlock>

            {/* 11 — Welfare */}
            <SectionBlock sectionKey="welfare" label="Welfare Assistance"
              status={sectionStatus.welfare} sectionRefs={sectionRefs}>
              <Row className="g-2">
                <Field label="Monthly welfare assistance received from the government"        value={r('welfare').govMonthlyStatus}   col={6} />
                <Field label="Monthly welfare providing government organisation"              value={r('welfare').govOrganisations}   col={6} />
                <Field label="Type of welfare assistance"                                     value={r('welfare').govWelfareTypes}    col={6} />
                <Field label="Total amount of financial assistance received per month"        value={r('welfare').govMonthlyAmount ? `Rs. ${r('welfare').govMonthlyAmount}` : ''} col={3} />
                <Field label="Receiving government assistance from (Date)"                    value={r('welfare').govStartDate}       col={3} />
                <Field label="Reason for stopped government assistance"                       value={r('welfare').govStopReason}      col={6} />
                <Field label="Reason for never receiving welfare assistance"                  value={r('welfare').govNeverReason}     col={6} />
                <Field label="Monthly payment from non-governmental organisations"            value={r('welfare').ngoMonthlyStatus}   col={6} />
                <Field label="Providing non-governmental entity"                              value={r('welfare').ngoEntities}        col={6} />
                <Field label="Organisation name(s) that has given monthly welfare"            value={r('welfare').ngoOrgNames}        col={6} freeText />
                <Field label="Total amount received per month from NGO"                       value={r('welfare').ngoMonthlyAmount ? `Rs. ${r('welfare').ngoMonthlyAmount}` : ''} col={3} />
                <Field label="Receiving NGO assistance from (Date)"                          value={r('welfare').ngoStartDate}       col={3} />
                <Field label="Have you received one-time welfare assistance?"                 value={r('welfare').oneTimeReceived}    col={4} />
                <Field label="Issued organisation type (one-time)"                           value={r('welfare').oneTimeOrgTypes}    col={4} />
                <Field label="Government entities name (one-time)"                           value={r('welfare').oneTimeGovEntities} col={6} />
                <Field label="Non-governmental entity name (one-time)"                       value={r('welfare').oneTimeNonGovName}  col={6} />
                <Field label="Total amount received from one-time support"                   value={r('welfare').oneTimeAmount ? `Rs. ${r('welfare').oneTimeAmount}` : ''} col={4} />
              </Row>
            </SectionBlock>

            {/* 12 — Rehabilitation */}
            <SectionBlock sectionKey="rehabilitation" label="Rehabilitation Plan"
              status={sectionStatus.rehabilitation} sectionRefs={sectionRefs}>
              <Row className="g-2">
                <Field label="Identified Need as per the Manual"                      value={r('rehabilitation').identifiedNeeds}             col={12} />
                <Field label="Intervention as per the Manual"                         value={r('rehabilitation').interventions}               col={12} />
                <Field label="Proposed Steps to Implement Identified Interventions"   value={r('rehabilitation').proposedSteps}               col={12} />
                <Field label="How to do it?"                                          value={r('rehabilitation').howToImplement}              col={12} freeText />
                <Field label="Duration (Years) of Rehabilitation Plan"                value={r('rehabilitation').durationYears}               col={3} />
                <Field label="Referrals of Rehabilitation Plan"                       value={r('rehabilitation').referrals}                   col={9} />
                <Field label="Other Aids for Rehabilitation Plan"                     value={r('rehabilitation').otherAids}                   col={6} />
                <Field label="Received Assistance from Provincial Council"            value={r('rehabilitation').provincialCouncilAssistance} col={6} />
              </Row>
            </SectionBlock>

          </div>{/* end sections */}

          {/* ── Document footer (captured separately) ── */}
          <div ref={footerRef}
            style={{ background: '#fff', padding: '8px 24px 12px', borderTop: '1px solid #dee2e6', marginTop: '4px' }}>
            <p className="text-muted text-center mb-0" style={{ fontSize: '0.75rem' }}>
              Generated from the DoSS National CBR Database · For official use only ·{' '}
              {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}
            </p>
          </div>

        </div>
      </div>

      {/* ── Mark as Deceased confirmation modal ── */}
      <Modal show={showDeceasedModal} onHide={() => { setShowDeceasedModal(false); setConfirmInput(''); setDeathDate(''); }} centered>
        <Modal.Header closeButton style={{ borderBottom: '2px solid #dc3545' }}>
          <Modal.Title className="text-danger">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>Mark as Deceased
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-1">You are about to mark the following record as deceased:</p>
          <div className="p-3 mb-3 rounded" style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>
            <div className="fw-semibold">{data.basic?.record?.name}</div>
            <div className="text-muted small">ID: {recordId}</div>
          </div>
          <p className="text-danger small fw-semibold mb-3">
            <i className="bi bi-lock-fill me-1"></i>
            This action is permanent. The record will become read-only and cannot be updated.
          </p>
          <Form.Group className="mb-3">
            <Form.Label className="small fw-semibold">Date of Death <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="date"
              value={deathDate}
              max={new Date().toISOString().slice(0, 10)}
              onChange={e => setDeathDate(e.target.value)}
            />
            <Form.Text className="text-muted">Enter the actual date the person passed away.</Form.Text>
          </Form.Group>
          <Form.Group>
            <Form.Label className="small">To confirm, type the person's ID below:</Form.Label>
            <Form.Control
              value={confirmInput}
              onChange={e => setConfirmInput(e.target.value)}
              placeholder={recordId}
              autoComplete="off"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => { setShowDeceasedModal(false); setConfirmInput(''); setDeathDate(''); }}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={confirmInput.trim() !== recordId || !deathDate || markingDeceased}
            onClick={handleMarkDeceased}
          >
            {markingDeceased
              ? <><Spinner animation="border" size="sm" className="me-2" />Marking…</>
              : <><i className="bi bi-x-circle me-1"></i>Confirm — Mark as Deceased</>}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionPill({ label, status }) {
  const colors = { pending: '#adb5bd', loading: '#0d6efd', done: '#198754', error: '#dc3545' };
  const icons  = { pending: '○', loading: '◌', done: '✓', error: '✗' };
  return (
    <span style={{
      fontSize: '0.7rem', padding: '2px 8px', borderRadius: '99px',
      background: colors[status] + '22', color: colors[status],
      border: `1px solid ${colors[status]}44`, whiteSpace: 'nowrap',
    }}>
      {icons[status]} {label}
    </span>
  );
}

// SectionBlock attaches its own ref into the shared sectionRefs object
function SectionBlock({ sectionKey, label, status, children, sectionRefs }) {
  return (
    <div
      ref={(el) => { sectionRefs.current[sectionKey] = el; }}
      style={{ background: '#fff', padding: '12px 24px', marginBottom: '2px' }}
    >
      {/* Section heading */}
      <div className="mb-2 px-2 py-1 rounded d-flex align-items-center justify-content-between"
        style={{
          background: '#f0f4ff',
          borderLeft: '4px solid #0d6efd',
          fontSize: '0.78rem', fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: '0.06em', color: '#333',
        }}
      >
        <span>{label}</span>
        {status === 'loading' && <Spinner animation="border" size="sm" style={{ width: '13px', height: '13px' }} />}
        {status === 'error'   && <span style={{ color: '#dc3545', fontSize: '0.72rem' }}>Failed to load</span>}
      </div>

      {status === 'done' && children}

      {status === 'loading' && (
        <div className="d-flex gap-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="rounded flex-grow-1"
              style={{ height: '48px', background: '#f0f0f0', opacity: 0.5 + i * 0.15 }} />
          ))}
        </div>
      )}

      {status === 'error' && (
        <p className="text-muted fst-italic mb-0" style={{ fontSize: '0.85rem' }}>
          This section could not be loaded.
        </p>
      )}
    </div>
  );
}

// Field — question in italic grey, answer in bold below.
// freeText preserves whitespace (address, textarea).
// All values are plain text — commas are just commas.
function Field({ label, value, col = 6, freeText = false }) {
  const empty   = !value || String(value).trim() === '';
  const display = empty ? null : String(value).trim();

  // Highlight "Other: ..." in the answer without any pill/badge
  const renderAnswer = (text) => {
    if (text.startsWith('Other:')) {
      const custom = text.replace(/^Other:\s*/, '');
      return (
        <span style={{ fontSize: '0.88rem', fontWeight: 600 }}>
          Other: <span style={{ color: '#6f4f00' }}>{custom}</span>
        </span>
      );
    }
    return <span style={{ fontSize: '0.88rem', fontWeight: 600 }}>{text}</span>;
  };

  return (
    <Col xs={12} sm={col}>
      <div style={{
        background: '#f8f9fa',
        border: '1px solid #e9ecef',
        borderRadius: '6px',
        padding: '6px 10px',
        height: '100%',
      }}>
        {/* Question */}
        <div style={{ fontSize: '0.72rem', color: '#666', fontStyle: 'italic', marginBottom: '3px', lineHeight: 1.35 }}>
          {label}
        </div>

        {/* Answer */}
        {empty ? (
          <span style={{ color: '#aaa', fontSize: '0.85rem' }}>—</span>
        ) : freeText ? (
          <div style={{ fontSize: '0.88rem', fontWeight: 500, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {display}
          </div>
        ) : (
          renderAnswer(display)
        )}
      </div>
    </Col>
  );
}

export default PersonalSummary;